import { memo, useEffect, useRef } from 'react';

import {
  CellEditingStartedEvent,
  CellKeyDownEvent,
  CellValueChangedEvent,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community';
import { AgGridReact, CustomDetailCellRendererProps } from 'ag-grid-react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { WithLocalStorage } from '@/components/hoc';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import {
  FertilizationWithEmbryos,
  TOA_GetFertilizationsForFreezing,
} from '@/rtk-query.gql';
import { useContextMenu } from '@/utils/context-menu';

import { useFreezeContext } from '../hooks/useFreezeContext';
import { freezeColDefs } from '../utils/columnDefs';
import { FIELD_NAMES, rowClassRules } from '../utils/constants';
import FreezeEmbryoGrid from './FreezeEmbryoGrid';

export const FreezeFertilizationGrid = memo(
  ({
    gridRef,
    fertilizationData,
    fertEmbryosData,
    handleUpdateFertilization,
    onGridReady,
    onCellKeyDown,
    handleEmbryoGridEdit,
    selectFertForLiveEmbryos,
    selectFertForTempEmbryos,
    handleFertilizationSelectionChanged,
    fertilizationSiteId,
    isUpdatingFertilizations,
    mode,
    gridApi,
    refetchFertEmbryoData,
  }: {
    gridRef: React.RefObject<AgGridReact>;
    fertilizationData: TOA_GetFertilizationsForFreezing[] | undefined;
    fertEmbryosData: FertilizationWithEmbryos[];
    handleUpdateFertilization: (params: CellValueChangedEvent) => void;
    onGridReady: (params: GridReadyEvent) => void;
    onCellKeyDown: (e: CellKeyDownEvent) => void;
    handleEmbryoGridEdit: (fertId: string) => void;
    selectFertForLiveEmbryos: (fertId: string) => void;
    selectFertForTempEmbryos: (fertId: string) => void;
    handleFertilizationSelectionChanged: () => void;
    fertilizationSiteId: string | null;
    isUpdatingFertilizations: boolean;
    embryosLoading: boolean;
    mode: string;
    gridApi: GridApi<TOA_GetFertilizationsForFreezing> | null;
    refetchFertEmbryoData: () => void;
    dataVersion: number;
  }) => {
    const defaultGridOptions = useDefaultGridOptions();
    const dispatch = useDispatch();
    const { form } = useFreezeContext();

    // Stable context ref so ag-grid doesn't reset focus on re-renders
    // caused by mutation state changes during cell edits.
    const stableContextRef = useRef<Record<string, unknown> | null>(null);
    if (!stableContextRef.current) {
      stableContextRef.current = {};
    }
    Object.assign(stableContextRef.current, {
      rawFertilizationSiteId: fertilizationSiteId,
      freezeDay: form.freezeDay,
      storageLocation: form.storageLocation,
      predictionTech: form.predictionTech,
      grader: form.grader,
      freezeTech: form.freezeTech,
      freezer: form.freezer,
      embryosPerVitStraw: form.embryosPerVitStraw,
      embryosPerStraw: form.embryosPerStraw,
      dt: form.dt,
    });

    useEffect(() => {
      if (fertilizationData) {
        gridRef.current?.api?.hideOverlay();
      } else {
        gridRef.current?.api?.showLoadingOverlay();
      }
    }, [fertilizationData, gridRef]);

    const handleCellEditingStarted = (params: CellEditingStartedEvent) => {
      const restrictedFields = [
        FIELD_NAMES.CLEAVED,
        FIELD_NAMES.QUALITY,
        FIELD_NAMES.LOW,
        FIELD_NAMES.HELD_FREEZE,
      ];
      const field = params.column.getColDef().field as string;
      if (restrictedFields.includes(field) && !form.predictionTech) {
        dispatch(
          addToast({
            severity: 'error',
            summary: 'Validation Error',
            detail:
              'Please fill in Prediction Tech first before updating this field.',
            life: 3000,
          }),
        );
        params.api.stopEditing();
        return;
      }
    };

    const { getContextMenuItems } = useContextMenu({ idKey: 'fertId' });

    return isUpdatingFertilizations ? (
      <AgGridReact
        gridOptions={defaultGridOptions}
        loadingOverlayComponent={ProgressSpinner}
        className={`ag-theme-transova${mode === 'dark' ? '-dark' : ''} medium-font__grid`}
      />
    ) : (
      <WithLocalStorage storageKey="freeze-grid">
        <AgGridReact
          className={`ag-theme-transova${mode === 'dark' ? '-dark' : ''} medium-font__grid performance-optimized`}
          rowData={fertilizationData}
          defaultColDef={defaultColDef}
          columnDefs={freezeColDefs}
          onCellEditingStarted={handleCellEditingStarted}
          onCellValueChanged={handleUpdateFertilization}
          gridOptions={defaultGridOptions}
          rowBuffer={20}
          rowHeight={45}
          ref={gridRef}
          suppressRowClickSelection={true}
          rowClassRules={rowClassRules}
          masterDetail={true}
          isRowMaster={() => true}
          onGridReady={onGridReady}
          cacheBlockSize={50}
          maxBlocksInCache={10}
          animateRows={false}
          rowModelType="clientSide"
          onCellKeyDown={onCellKeyDown}
          detailCellRenderer={(params: CustomDetailCellRendererProps) => {
            const fertData = fertEmbryosData?.find(
              (fert) => fert.fertId === params.data.fertId,
            );

            if (!fertData) {
              return null;
            }

            return (
              <FreezeEmbryoGrid
                key={params.data.fertId}
                rowData={params.data}
                onEdit={(fertId: string) => handleEmbryoGridEdit(fertId)}
                onLiveEmbryoSelectionChanged={(fertId) =>
                  selectFertForLiveEmbryos(fertId)
                }
                onTempEmbryoSelectionChanged={(fertId) =>
                  selectFertForTempEmbryos(fertId)
                }
                isExport={params.data.export !== 'No'}
                index={params.data.index}
                refetchFertEmbryoData={refetchFertEmbryoData}
                gridApi={gridApi as GridApi<TOA_GetFertilizationsForFreezing>}
                fertEmbryosData={fertEmbryosData}
                fertGridRef={
                  gridRef as unknown as React.MutableRefObject<AgGridReact>
                }
                node={params.node}
                api={params.api}
                fertilizationIds={fertilizationData?.map(
                  (fert) => fert.fertId as string,
                )}
              />
            );
          }}
          detailRowAutoHeight={true}
          rowSelection="multiple"
          getRowId={(params: GetRowIdParams) => params.data.fertId}
          getContextMenuItems={getContextMenuItems}
          onSelectionChanged={handleFertilizationSelectionChanged}
          context={stableContextRef.current}
          suppressColumnVirtualisation={false}
          suppressRowVirtualisation={false}
          suppressDragLeaveHidesColumns={true}
          suppressBrowserResizeObserver={true}
          suppressAnimationFrame={true}
          suppressScrollOnNewData={true}
          suppressModelUpdateAfterUpdateTransaction={true}
          suppressAggFuncInHeader={true}
          suppressRowTransform={true}
          suppressColumnMoveAnimation={true}
          suppressRowHoverHighlight={true}
          suppressRowDeselection={true}
          suppressRowDrag={true}
        />
      </WithLocalStorage>
    );
  },
);
